import React, {
  ReactElement,
  useState,
  useCallback,
  useMemo,
  Fragment,
} from 'react';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useLocation } from 'react-router-dom';

import {
  Badge,
  ReactTable,
  CustomCancelModal,
  ConfirmAlert,
} from 'src/ui/components';

import { usePersonAlertsAccounts } from '../../api';
import useAccountInfoColumns from './useAccountInfoColumns';

import { usePersonAlertStore } from '../../store/personAlertStore';
import { noAccountWarning } from '../../utils/addEditPersonAlertConstant';

const sortItem = { sortKey: 'CID', sortOrder: 'asc' };

function AccountSelection(): ReactElement {
  const [columnsSearch, setColumnsSearch] = useState<Record<string, string>>(
    {},
  );
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);
  const [isScroll, setIsScroll] = useState<boolean>(false);
  const [rowIndex, setRowIndex] = useState<number>();

  const [pendingRowItem, setPendingRowItem] = useState<any | null>(null);
  // to check if it is reset button or row click
  const [modalContext, setModalContext] = useState<string>('');
  // for custom yes or no modal
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  //for confirm alert modal
  const [showSecondaryConfirm, setShowSecondaryConfirm] = useState(false);

  const userMode = usePersonAlertStore(
    useCallback(state => state.userMode, []),
  );

  const location = useLocation();

  const isEdit = useMemo(() => {
    return location.pathname.split('/').includes('Edit');
  }, [location.pathname]);

  const isSaveClicked = usePersonAlertStore(
    useCallback(state => state.isSaveClicked, []),
  );

  const enableAlertTabs = usePersonAlertStore(
    useCallback(state => state.enableAlertTabs, []),
  );

  const deletePersonDetails = usePersonAlertStore(
    useCallback(state => state.deletePersonDetails, []),
  );

  const setSearchPerson = usePersonAlertStore(
    useCallback(state => state?.setSearchPerson, []),
  );

  const setShowTable = usePersonAlertStore(
    useCallback(state => state?.setShowTable, []),
  );

  const setSelectedRowId = usePersonAlertStore(
    useCallback(state => state?.setSelectedRowId, []),
  );

  const selectedAccountId = usePersonAlertStore(
    useCallback(state => state?.selectedAccountId, []),
  );

  const setSelectedAccountId = usePersonAlertStore(
    useCallback(state => state?.setSelectedAccountId, []),
  );

  const setNewPerson = usePersonAlertStore(
    useCallback(state => state?.setNewPerson, []),
  );

  const newPerson = usePersonAlertStore(
    useCallback(state => state?.newPerson, []),
  );

  const person = usePersonAlertStore(
    useCallback(state => state?.alertDetails?.Person?.PersonAlertDetails, []),
  );

  const personAlertAllDet = usePersonAlertStore(
    useCallback(state => state?.alertDetails?.Person, []),
  );

  const deleteAlertDetails = usePersonAlertStore(
    useCallback(state => state?.deleteAlertDetails, []),
  );

  const clearOptionsByAccount = usePersonAlertStore(
    useCallback(state => state?.clearOptionsByAccount, []),
  );

  const setErrorMsgFalse = usePersonAlertStore(
    useCallback(state => state?.setErrorMsgFalse, []),
  );

  // To check if custom cancel modal should be shown on account reset or not
  const shouldShowCancelModal = useMemo(() => {
    return (
      person === undefined &&
      newPerson === true &&
      Object.keys(personAlertAllDet)?.length === 0
    );
  }, [person, newPerson, personAlertAllDet]);

  // to check if the secondary modal should show on reseting - if an existing person is selected
  const shouldShowSecondaryModal = useMemo(() => {
    return newPerson === false && person !== undefined;
  }, [newPerson, person]);

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isAccountListLoading,
  } = usePersonAlertsAccounts({
    columnsSearch,
    currentSortItem,
  });

  const filteredData = useMemo(() => {
    if (data?.pages[0]?.Data) {
      return data?.pages?.map(page => page?.Data).flat();
    } else return [];
  }, [data]);

  const accountsInfo = usePersonAlertStore(
    React.useCallback(state => state.accountsInfo, []),
  );

  console.log('accountsInfo', accountsInfo);

  const updateAccountInfo = usePersonAlertStore(
    React.useCallback(state => state.updateAccountInfo, []),
  );
  // alert message - switching account
  const alertMessage = useMemo(() => {
    return `You’ve switched your account selection from ${
      accountsInfo?.CID ?? ''
    }${accountsInfo?.Name !== '' ? ` - ${accountsInfo?.Name}` : ''} to ${
      pendingRowItem?.original?.CID ?? ''
    }${
      pendingRowItem?.original?.Name !== ''
        ? ` - ${pendingRowItem?.original?.Name}`
        : ''
    }. Do you want to continue using the previously entered details for adding a new person?`;
  }, [
    accountsInfo?.CID,
    accountsInfo?.Name,
    pendingRowItem?.original?.CID,
    pendingRowItem?.original?.Name,
  ]);

  // alert message - resetting account - if new peron is false
  const resetAlertMessage = useMemo(() => {
    return `This action will remove entered details for adding a new person. Do you want to proceed?`;
  }, []);

  // alert message - resetting account - if new peron is fasle and an existing person is selected
  const resetExistingPersonAlertMessage = useMemo(() => {
    return `Resetting the account selection will remove the details of the person already selected`;
  }, []);

  //searching function callback
  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  //if filteres applied
  const isFilterApplied = useMemo(() => {
    if (
      currentSortItem?.sortKey === 'CID' &&
      currentSortItem?.sortOrder === 'asc' &&
      ((Object.keys(columnsSearch).length !== 0 &&
        Object.values(columnsSearch).some((x: any) => x === '')) ||
        Object.keys(columnsSearch).length === 0)
    ) {
      return false;
    } else return true;
  }, [columnsSearch, currentSortItem?.sortKey, currentSortItem?.sortOrder]);

  //sorting function callback
  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const isObjectDirty = (obj: any): boolean => {
    if (obj == null) return false;

    if (typeof obj === 'string') return obj.trim() !== '';
    if (typeof obj === 'number') return obj !== 0;
    if (typeof obj === 'boolean') return obj !== false;
    if (obj instanceof Date) return !isNaN(obj.getTime());

    if (typeof obj === 'object') {
      return Object.values(obj).some(value => isObjectDirty(value));
    }

    return false;
  };

  const isFirstAndLastNameDirty = (data: any): boolean => {
    return isObjectDirty(data);
  };

  //reset function
  const handleReset = () => {
    setModalContext('reset');
    if (shouldShowCancelModal === true) {
      setShowCancelModal(true);
    }

    const dirty = isFirstAndLastNameDirty(personAlertAllDet);
    if (
      newPerson === false &&
      personAlertAllDet !== undefined &&
      Object.keys(personAlertAllDet)?.length > 0
    ) {
      setShowSecondaryConfirm(true);
      return;
    }
    if (newPerson === true && dirty) {
      setShowCancelModal(true);
      return;
    }
    handleCancelYesOnReset();
  };

  // React-table column list
  const columns = useAccountInfoColumns(
    setColumnsSearch,
    setCurrentSortItem,
    handleReset,
  );

  //react table row click
  function handleRowClick(item: any) {
    const clickedAccountId = item?.original?.Id;
    const dirty = isFirstAndLastNameDirty(personAlertAllDet);
    // Ignore if the same row is clicked
    setModalContext('rowClick');
    if (clickedAccountId === selectedAccountId) return;
    if (
      item?.original.LimitCategory !== null &&
      item?.original?.AccountCategoryLimitingValues === null
    )
      return;

    if (person === undefined && newPerson === false) {
      switchAccount(item);
      return;
    }

    if (newPerson === true && dirty) {
      clearOptionsByAccount();
      setPendingRowItem(item);
      setShowCancelModal(true);
      return;
    }

    if (newPerson && person === undefined && !dirty) {
      setNewPerson(false);
      switchAccount(item);
      return;
    }

    if (!newPerson && person !== undefined) {
      clearOptionsByAccount();
      setPendingRowItem(item);
      setShowSecondaryConfirm(true);
    }
  }

  function switchAccount(item: any) {
    const clickedAccountId = item?.original?.Id;
    setSelectedAccountId(clickedAccountId);
    updateAccountInfo && updateAccountInfo(item?.original);
    setRowIndex(item?.index);
    clearOptionsByAccount();
  }

  function handleCancelYes() {
    if (!pendingRowItem) return;
    setShowCancelModal(false);
    const clickedAccountId = pendingRowItem?.original?.Id;
    setSelectedAccountId(clickedAccountId);
    updateAccountInfo && updateAccountInfo(pendingRowItem?.original);
    setRowIndex(pendingRowItem?.index);
    setPendingRowItem(null);
    setModalContext('');
  }
  function handleCancelNo() {
    if (!pendingRowItem) return;
    const clickedAccountId = pendingRowItem?.original?.Id;
    setShowCancelModal(false);
    setSelectedAccountId(clickedAccountId);
    updateAccountInfo && updateAccountInfo(pendingRowItem?.original);
    setRowIndex(pendingRowItem?.index);
    setNewPerson(false);
    deletePersonDetails();
    setSearchPerson('');
    setShowTable(true);
    setSelectedRowId(-1);
    setPendingRowItem(null);
    setModalContext('');
    clearOptionsByAccount();
  }

  function handleCancelYesOnReset() {
    setShowCancelModal(false);
    setErrorMsgFalse();
    setColumnsSearch({});
    setCurrentSortItem({ sortKey: 'CID', sortOrder: 'asc' });
    updateAccountInfo && updateAccountInfo(undefined);
    deletePersonDetails();
    clearOptionsByAccount();
    setNewPerson(false);
    setSearchPerson('');
    setShowTable(true);
    setSelectedRowId(-1);
    setSelectedAccountId(-1);
    setModalContext('');
  }

  function handleCancelNoOnReset() {
    setShowCancelModal(false);
    setModalContext('');
  }

  function onEndReached() {
    if (hasNextPage) {
      setIsScroll(true);
      fetchNextPage();
    } else if (!hasNextPage && isScroll) {
      setIsScroll(false);
    }
  }

  function renderAccountList() {
    return (
      <Fragment>
        <div className="flex grow flex-wrap items-center gap-x-4 gap-y-2  pr-2">
          {isSaveClicked && !enableAlertTabs('accounts-tab') && (
            <div className="h-3 w-3">
              <span className="relative flex h-3 w-3 items-center justify-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
            </div>
          )}
          <div className="grow">
            <h3 className="grow font-medium after:ml-0.5 after:text-red-500 after:content-['*'] dark:after:text-red-400">
              Select account
            </h3>
          </div>

          <div>
            {accountsInfo !== undefined ? (
              <div className="flex flex-wrap items-center gap-2 ">
                <label className="block text-xs font-bold">
                  Selected account
                </label>
                <Badge key={`${accountsInfo?.Id}`}>
                  {accountsInfo?.CID}
                  {accountsInfo.Name !== '' ? `-${accountsInfo?.Name}` : ''}
                </Badge>
              </div>
            ) : (
              !enableAlertTabs('accounts-tab') && (
                <p className="text-xs opacity-80">{noAccountWarning}</p>
              )
            )}
          </div>
        </div>
        {showCancelModal && (
          <CustomCancelModal
            showCancelModal={showCancelModal}
            setShowCancelModal={setShowCancelModal}
            //for yes
            handleCancel={
              modalContext === 'rowClick'
                ? handleCancelYes
                : handleCancelYesOnReset
            }
            // for no
            handleNo={
              modalContext === 'rowClick'
                ? handleCancelNo
                : handleCancelNoOnReset
            }
            alertMessage={
              modalContext === 'rowClick' ? alertMessage : resetAlertMessage
            }
          />
        )}
        {showSecondaryConfirm && (
          <ConfirmAlert
            message={
              modalContext === 'rowClick'
                ? 'Switching accounts will remove the details of the person already selected.'
                : resetExistingPersonAlertMessage
            }
            showModel={showSecondaryConfirm}
            setShowModel={setShowSecondaryConfirm}
            buttonSet={[
              {
                label: 'OK',
                handleClick: () => {
                  setShowSecondaryConfirm(false);
                  if (
                    shouldShowSecondaryModal === true &&
                    modalContext === 'reset'
                  ) {
                    handleCancelYesOnReset();
                  }
                  // delete person details
                  deletePersonDetails();
                  setSearchPerson('');
                  setShowTable(true);
                  setSelectedRowId(-1);

                  if (pendingRowItem) {
                    switchAccount(pendingRowItem);
                    setPendingRowItem(null);
                  }
                },
                variant: 'primary',
              },
            ]}
          />
        )}
      </Fragment>
    );
  }

  function renderSelectedAccount() {
    return (
      <div
        tabIndex={0}
        className={`relative flex flex-wrap rounded bg-white shadow focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-blue-600 dark:bg-neutral-800 dark:focus-visible:outline-blue-400`}
      >
        <div className="w-1/2 min-w-[92px] p-2 sm:grow md:w-1/4">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            Account name
          </label>
          <p className="mt-0.5 text-xs font-bold tracking-wide text-black dark:text-white">
            {accountsInfo?.Name}
          </p>
        </div>
        <div className="w-1/2 min-w-[135px] p-2 sm:grow md:w-1/4">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            CID
          </label>
          <p className="mt-0.5 text-xs font-bold tracking-wide text-black dark:text-white">
            {accountsInfo?.CID}
          </p>
        </div>
        <div className="w-1/2 min-w-[135px] p-2 sm:grow md:w-1/4">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            SID
          </label>
          <p className="mt-0.5 text-xs font-bold tracking-wide text-black dark:text-white">
            {accountsInfo?.SID}
          </p>
        </div>
        <div className="w-1/2 min-w-[155px] p-2 sm:grow md:w-1/4">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            PID
          </label>
          <p className="mt-0.5 text-xs font-bold tracking-wide text-black dark:text-white">
            {accountsInfo?.PID}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Disclosure
      as="div"
      id="account-info"
      defaultOpen={true}
      className="scroll-m-4"
    >
      {({ open }) => (
        <>
          <Disclosure.Button className="flex w-full items-center justify-between rounded-lg bg-white px-4 py-2 text-left shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-light-focus disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 ui-open:rounded-b-none dark:bg-neutral-800  dark:hover:bg-neutral-800/70">
            {renderAccountList()}
            <ChevronDownIcon
              className={`${
                open ? 'rotate-180 transform' : ''
              } h-5 w-5 text-gray-500`}
            />
          </Disclosure.Button>

          <Disclosure.Panel
            className={`rounded-b-lg border-t bg-gray-50 text-sm text-gray-500 shadow dark:border-neutral-700/50  dark:bg-neutral-800 ${
              userMode === 'Edit' ? ' ' : 'ui-open:min-h-[300px]'
            }`}
          >
            <div className="max-w-full scroll-smooth">
              {userMode === 'Edit' && accountsInfo !== undefined ? (
                <div className="rounded-b-lg bg-gray-100 p-3 dark:bg-neutral-900 ">
                  {renderSelectedAccount()}
                </div>
              ) : (
                <ReactTable
                  columns={columns ? columns : []}
                  data={filteredData ? filteredData : []}
                  columnsSearch={columnsSearch}
                  headerUpdateCallBack={headerUpdateCallBack}
                  handleSortChange={handleSortChange}
                  sortedItem={currentSortItem}
                  isLoading={isAccountListLoading}
                  isFetchingNextPage={isFetchingNextPage}
                  onRowClick={handleRowClick}
                  onEndReached={onEndReached}
                  className="max-h-72 max-w-full overflow-x-auto"
                  selectedRowId={accountsInfo?.Id}
                  rowIndex={rowIndex}
                  from="PersonAlert"
                />
              )}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

export { AccountSelection };
